import React from "react";
import { Button, Card, Form, Collapse } from "react-bootstrap";
import { FiTrash2, FiChevronDown } from "react-icons/fi";
import { FaGripVertical } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Phase, DeleteTarget, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import PartComponent from "./PartComponent";

interface PhaseComponentProps {
  phase: Phase;
  phaseIndex: number;
  phaseId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    [SUBTASK_TYPE.VIDEO]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.NOTES]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.MCQ]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.PROJECT_CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onNameChange: (name: string) => void;
  allPhases?: Phase[];
  onAddPart: () => void;
  onDelete: (target: DeleteTarget) => void;
  onPartDragEnd: (result: DropResult) => void;
  onPartToggleExpand: (partId: string) => void;
  onPartNameChange: (partIndex: number, name: string) => void;
  onAddTask: (partIndex: number) => void;
  onPartDelete: (target: DeleteTarget) => void;
  onTaskDragEnd: (partIndex: number, result: DropResult) => void;
  onTaskToggleExpand: (taskId: string) => void;
  onTaskNameChange: (partIndex: number, taskIndex: number, name: string) => void;
  onAddSubtask: (partIndex: number, taskIndex: number) => void;
  onTaskDelete: (target: DeleteTarget) => void;
  onSubtaskDragEnd: (partIndex: number, taskIndex: number, result: DropResult) => void;
  onSelectOrAssign: (partIndex: number, taskIndex: number, subtaskIndex: number, actionType: "select" | "assign") => void;
  onSubtaskNameChange: (partIndex: number, taskIndex: number, subtaskIndex: number, name: string) => void;
  onMandatoryChange: (partIndex: number, taskIndex: number, subtaskIndex: number, checked: boolean) => void;
  onSubjectChange: (partIndex: number, taskIndex: number, subtaskIndex: number, subjectId: string) => void;
  onTopicChange: (partIndex: number, taskIndex: number, subtaskIndex: number, topicId: string) => void;
  onSubtopicChange: (partIndex: number, taskIndex: number, subtaskIndex: number, subtopicId: string) => void;
  onSubtaskTypeChange: (partIndex: number, taskIndex: number, subtaskIndex: number, subtaskType: string) => void;
  onEditorChange: (partIndex: number, taskIndex: number, subtaskIndex: number, editor: string) => void;
  onLevelChange: (partIndex: number, taskIndex: number, subtaskIndex: number, level: string) => void;
  onContentLevelChange: (partIndex: number, taskIndex: number, subtaskIndex: number, level: string) => void;
  onContentCountChange: (partIndex: number, taskIndex: number, subtaskIndex: number, level: string, count: string) => void;
  onAddContent: (partIndex: number, taskIndex: number, subtaskIndex: number) => void;
  onFetchContent: (partIndex: number, taskIndex: number, subtaskIndex: number, subtopicId: string) => Promise<void>;
  onContentSelected: (partIndex: number, taskIndex: number, subtaskIndex: number, contentId: string, level: string, time: string) => void;
  onPreviewContent: (partIndex: number, taskIndex: number, subtaskIndex: number, type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES, content: any) => void;
  onContentCreated: (partIndex: number, taskIndex: number, subtaskIndex: number, level1: string[], level2: string[], level3: string[]) => void;
  onSubtaskDelete: (target: DeleteTarget) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  onDescriptionChange?: (partIndex: number, taskIndex: number, subtaskIndex: number, description: string) => void;
  onCcChange?: (partIndex: number, taskIndex: number, subtaskIndex: number, ccId: string) => void;
  assignedCcIds?: string[] | null;
  generateId: (prefix: string, index: number) => string;
  isPartExpanded: (partId: string) => boolean;
  isTaskExpanded: (taskId: string) => boolean;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
  isPhaseRestricted?: (phaseId: string) => boolean;
  isPartRestricted?: (phaseId: string, partId: string) => boolean;
  isTaskRestricted?: (phaseId: string, partId: string, taskId: string) => boolean;
  isSubtaskRestricted?: (phaseId: string, partId: string, taskId: string, subtaskId: string) => boolean;
}

/**
 * PhaseComponent: Renders a phase card with drag-and-drop support for parts
 * This component displays a single phase and manages its parts.
 * It provides functionality for:
 * - Expanding/collapsing the phase
 * - Editing the phase name
 * - Adding new parts
 * - Deleting the phase
 * - Drag-and-drop reordering of parts
 * - Rendering PartComponent children for each part
 */
const PhaseComponent: React.FC<PhaseComponentProps> = ({
  phase,
  phaseIndex,
  phaseId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onNameChange,
  onAddPart,
  onDelete,
  onPartDragEnd,
  onPartToggleExpand,
  onPartNameChange,
  onAddTask,
  onPartDelete,
  onTaskDragEnd,
  onTaskToggleExpand,
  onTaskNameChange,
  onAddSubtask,
  onTaskDelete,
  onSubtaskDragEnd,
  onSelectOrAssign,
  onSubtaskNameChange,
  onMandatoryChange,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onSubtaskTypeChange,
  onEditorChange,
  onLevelChange,
  onContentLevelChange,
  onContentCountChange,
  onAddContent,
  onFetchContent,
  onContentSelected,
  onPreviewContent,
  onContentCreated,
  onSubtaskDelete,
  onFetchTopics,
  generateId,
  isPartExpanded,
  isTaskExpanded,
  isSubtaskExpanded,
  toggleSubtaskExpand,
  onDescriptionChange,
  onCcChange,
  assignedCcIds,
  isPhaseRestricted,
  isPartRestricted,
  isTaskRestricted,
  isSubtaskRestricted,
  allPhases = []
}) => {
  const phaseRestricted = isPhaseRestricted ? isPhaseRestricted(phaseId) : false;
  
  // Check for duplicate phase names
  const isDuplicatePhaseName = phase.name && phase.name.trim() !== "" && 
    allPhases.some((p, idx) => idx !== phaseIndex && p.name && p.name.trim().toLowerCase() === phase.name.trim().toLowerCase());
  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div {...dragHandleProps} style={{ cursor: phaseRestricted ? 'not-allowed' : 'grab' }}>
            <FaGripVertical />
          </div>
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{phaseId ? phaseId.charAt(0).toUpperCase() + phaseId.slice(1) : phaseId}</span>
          <div>
            <Form.Control
              type="text"
              placeholder="Phase Name"
              value={phase.name}
              onChange={(e) => onNameChange(e.target.value)}
              style={{ width: "200px" }}
            />
            {isDuplicatePhaseName && (
              <div className="text-warning mt-2">
                Phase name has already been used
              </div>
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onAddPart}
          >
            + Part
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete({
              type: "phase",
              id: phaseId,
              name: phase.name || `Phase ${phaseIndex + 1}`,
              phaseIndex
            })}
            disabled={phaseRestricted}
          >
            <FiTrash2 size={16} />
          </Button>
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          <DragDropContext onDragEnd={onPartDragEnd}>
            <Droppable droppableId={`parts-${phaseId}`}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {phase.parts.map((part, partIndex) => {
                    const partId = `${phaseId}-${part.id}`;
                    return (
                      <Draggable 
                        key={part.id} 
                        draggableId={part.id} 
                        index={partIndex}
                        isDragDisabled={isPartRestricted ? isPartRestricted(phaseId, part.id) : false}
                      >
                        {(provided, snapshot) => {
                          const partRestricted = isPartRestricted ? isPartRestricted(phaseId, part.id) : false;
                          // Only apply opacity if part is restricted AND phase is NOT restricted (to avoid compounding)
                          const shouldApplyOpacity = partRestricted && !phaseRestricted;
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'transparent',
                                opacity: shouldApplyOpacity ? 0.6 : 1
                              }}
                            >
                            <PartComponent
                              part={part}
                              phaseIndex={phaseIndex}
                              partIndex={partIndex}
                              partId={partId}
                              phaseId={phaseId}
                              isExpanded={isPartExpanded(partId)}
                              projectId={projectId}
                              subjects={subjects}
                              topicsCache={topicsCache}
                              contentStatus={contentStatus}
                              dragHandleProps={provided.dragHandleProps}
                              onToggleExpand={() => onPartToggleExpand(partId)}
                              onNameChange={(name) => onPartNameChange(partIndex, name)}
                              onAddTask={() => onAddTask(partIndex)}
                              onDelete={onPartDelete}
                              onTaskDragEnd={(result) => onTaskDragEnd(partIndex, result)}
                              onTaskToggleExpand={onTaskToggleExpand}
                              onTaskNameChange={(taskIndex, name) => onTaskNameChange(partIndex, taskIndex, name)}
                              onAddSubtask={(taskIndex) => onAddSubtask(partIndex, taskIndex)}
                              onTaskDelete={onTaskDelete}
                              onSubtaskDragEnd={(taskIndex, result) => onSubtaskDragEnd(partIndex, taskIndex, result)}
                              onSelectOrAssign={(taskIndex, subtaskIndex, actionType) => onSelectOrAssign(partIndex, taskIndex, subtaskIndex, actionType)}
                              onSubtaskNameChange={(taskIndex, subtaskIndex, name) => onSubtaskNameChange(partIndex, taskIndex, subtaskIndex, name)}
                              onMandatoryChange={(taskIndex, subtaskIndex, checked) => onMandatoryChange(partIndex, taskIndex, subtaskIndex, checked)}
                              onSubjectChange={(taskIndex, subtaskIndex, subjectId) => onSubjectChange(partIndex, taskIndex, subtaskIndex, subjectId)}
                              onTopicChange={(taskIndex, subtaskIndex, topicId) => onTopicChange(partIndex, taskIndex, subtaskIndex, topicId)}
                              onSubtopicChange={(taskIndex, subtaskIndex, subtopicId) => onSubtopicChange(partIndex, taskIndex, subtaskIndex, subtopicId)}
                              onSubtaskTypeChange={(taskIndex, subtaskIndex, subtaskType) => onSubtaskTypeChange(partIndex, taskIndex, subtaskIndex, subtaskType)}
                              onEditorChange={(taskIndex, subtaskIndex, editor) => onEditorChange(partIndex, taskIndex, subtaskIndex, editor)}
                              onLevelChange={(taskIndex, subtaskIndex, level) => onLevelChange(partIndex, taskIndex, subtaskIndex, level)}
                              onContentLevelChange={(taskIndex, subtaskIndex, level) => onContentLevelChange(partIndex, taskIndex, subtaskIndex, level)}
                              onContentCountChange={(taskIndex, subtaskIndex, level, count) => onContentCountChange(partIndex, taskIndex, subtaskIndex, level, count)}
                              onAddContent={(taskIndex, subtaskIndex) => onAddContent(partIndex, taskIndex, subtaskIndex)}
                              onFetchContent={(taskIndex, subtaskIndex, subtopicId) => onFetchContent(partIndex, taskIndex, subtaskIndex, subtopicId)}
                              onContentSelected={(taskIndex, subtaskIndex, contentId, level, time) => onContentSelected(partIndex, taskIndex, subtaskIndex, contentId, level, time)}
                              onPreviewContent={(taskIndex, subtaskIndex, type, content) => onPreviewContent(partIndex, taskIndex, subtaskIndex, type, content)}
                              onContentCreated={(taskIndex, subtaskIndex, level1, level2, level3) => onContentCreated(partIndex, taskIndex, subtaskIndex, level1, level2, level3)}
                              onSubtaskDelete={onSubtaskDelete}
                              onFetchTopics={onFetchTopics}
                                                          onDescriptionChange={onDescriptionChange ? (taskIndex, subtaskIndex, description) => onDescriptionChange(partIndex, taskIndex, subtaskIndex, description) : undefined}
                              onCcChange={onCcChange ? (taskIndex, subtaskIndex, ccId) => onCcChange(partIndex, taskIndex, subtaskIndex, ccId) : undefined}
                              assignedCcIds={assignedCcIds}
                              generateId={generateId}
                              isTaskExpanded={isTaskExpanded}
                              isSubtaskExpanded={isSubtaskExpanded}
                              toggleSubtaskExpand={toggleSubtaskExpand}
                              isPartRestricted={isPartRestricted}
                              isTaskRestricted={isTaskRestricted}
                              isSubtaskRestricted={isSubtaskRestricted}
                              allParts={phase.parts}
                            />
                          </div>
                        );
                      }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default PhaseComponent;

